import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, AlertCircle, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import type { Asset } from '@/types/asset';
import { ASSET_STATUS_LABELS } from '@/types/asset';
import type { AssetSelectionFilter } from '@/types/depreciation';
import { dataService } from '@/services/data-service';

// Stable empty array to prevent infinite re-render loops when used as default prop
const EMPTY_STRING_ARRAY: string[] = [];

// Pagination constants
const ITEMS_PER_PAGE = 25;

// Sort configuration
type SortField = 'code' | 'name' | 'category' | 'subCategory' | 'location' | 'subLocation' | 'postingFrequency' | 'netBookValue' | 'status';
type SortDirection = 'asc' | 'desc' | null;

interface AssetSelectionDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onConfirm: (selectedAssets: Asset[]) => void;
  readonly title?: string;
  readonly description?: string;
  readonly excludeAssetIds?: string[]; // Asset IDs to exclude from selection (already in detail grid)
  readonly initialSelectedAssetIds?: string[]; // Asset IDs that are already selected (for editing)
  readonly confirmButtonLabel?: string; // Custom label for the confirm button
}

export function AssetSelectionDialog({ 
  open, 
  onOpenChange, 
  onConfirm,
  title = "Select Assets for Depreciation",
  description = "Choose one or more assets, or select all. You can refine the set later.",
  excludeAssetIds = EMPTY_STRING_ARRAY,
  initialSelectedAssetIds = EMPTY_STRING_ARRAY,
  confirmButtonLabel = "Add Selected Assets"
}: AssetSelectionDialogProps): React.ReactElement {
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set(initialSelectedAssetIds));
  const [filters, setFilters] = useState<AssetSelectionFilter>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load assets from API
  useEffect(() => {
    if (!open) return;

    const loadAssets = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await dataService.getAssets();
        // getAssets returns { data: Asset[], total, page, pageSize, totalPages }
        setAssets(response.data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load assets');
        setAssets([]);
      } finally {
        setIsLoading(false);
      }
    };

    void loadAssets();
  }, [open]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSelectedAssetIds(new Set(initialSelectedAssetIds));
      setSearchTerm('');
      setDebouncedSearchTerm('');
      setFilters({});
      setCurrentPage(1);
      setSortField(null);
      setSortDirection(null);
    }
  }, [open, initialSelectedAssetIds]);

  // Filter assets (excluding already added ones and disposed assets)
  const availableAssets = useMemo(() => {
    return assets.filter(asset => 
      !excludeAssetIds.includes(asset.id) && 
      asset.status !== 'disposed'
    );
  }, [assets, excludeAssetIds]);

  // Apply filters and search
  const filteredAssets = useMemo(() => {
    let filtered = availableAssets;

    // Apply category filter
    if (filters.category && filters.category.length > 0) {
      filtered = filtered.filter(asset => filters.category?.includes(asset.category));
    }

    // Apply sub-category filter
    if (filters.subCategory && filters.subCategory.length > 0) {
      filtered = filtered.filter(asset => 
        asset.subCategory && filters.subCategory?.includes(asset.subCategory)
      );
    }

    // Apply location filter
    if (filters.location && filters.location.length > 0) {
      filtered = filtered.filter(asset => filters.location?.includes(asset.location));
    }

    // Apply sub-location filter
    if (filters.subLocation && filters.subLocation.length > 0) {
      filtered = filtered.filter(asset => 
        asset.subLocation && filters.subLocation?.includes(asset.subLocation)
      );
    }

    // Apply posting frequency filter
    if (filters.postingFrequency && filters.postingFrequency.length > 0) {
      filtered = filtered.filter(asset => 
        asset.postingFrequency && filters.postingFrequency?.includes(asset.postingFrequency)
      );
    }

    // Apply search term
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(asset =>
        asset.code.toLowerCase().includes(searchLower) ||
        asset.name.toLowerCase().includes(searchLower) ||
        (asset.serialNumber && asset.serialNumber.toLowerCase().includes(searchLower))
      );
    }

    return filtered;
  }, [availableAssets, filters, debouncedSearchTerm]);

  // Apply sorting
  const sortedAssets = useMemo(() => {
    if (!sortField || !sortDirection) {
      return filteredAssets;
    }

    return [...filteredAssets].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (sortField) {
        case 'code':
          aValue = a.code;
          bValue = b.code;
          break;
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'category':
          aValue = a.category;
          bValue = b.category;
          break;
        case 'subCategory':
          aValue = a.subCategory ?? '';
          bValue = b.subCategory ?? '';
          break;
        case 'location':
          aValue = a.location;
          bValue = b.location;
          break;
        case 'subLocation':
          aValue = a.subLocation ?? '';
          bValue = b.subLocation ?? '';
          break;
        case 'postingFrequency':
          aValue = a.postingFrequency ?? '';
          bValue = b.postingFrequency ?? '';
          break;
        case 'netBookValue':
          aValue = a.netBookValue;
          bValue = b.netBookValue;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortDirection === 'asc'
          ? (aValue as number) - (bValue as number)
          : (bValue as number) - (aValue as number);
      }
    });
  }, [filteredAssets, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(sortedAssets.length / ITEMS_PER_PAGE);
  const paginatedAssets = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return sortedAssets.slice(startIndex, endIndex);
  }, [sortedAssets, currentPage]);

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page on sort
  };

  // Get sort icon for column
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-4 w-4 inline-block opacity-40" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="ml-1 h-4 w-4 inline-block" />;
    }
    return <ArrowDown className="ml-1 h-4 w-4 inline-block" />;
  };

  // Extract unique values for filter dropdowns
  const availableCategories = useMemo(() => 
    Array.from(new Set(availableAssets.map(a => a.category))).sort((a, b) => a.localeCompare(b)),
    [availableAssets]
  );

  const availableSubCategories = useMemo(() => 
    Array.from(new Set(availableAssets.map(a => a.subCategory).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b)),
    [availableAssets]
  );

  const availableLocations = useMemo(() => 
    Array.from(new Set(availableAssets.map(a => a.location))).sort((a, b) => a.localeCompare(b)),
    [availableAssets]
  );

  const availableSubLocations = useMemo(() => 
    Array.from(new Set(availableAssets.map(a => a.subLocation).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b)),
    [availableAssets]
  );

  const availablePostingFrequencies = useMemo(() => 
    Array.from(new Set(availableAssets.map(a => a.postingFrequency).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b)),
    [availableAssets]
  );

  // Handle individual asset selection
  const handleSelectAsset = (assetId: string, checked: boolean) => {
    setSelectedAssetIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(assetId);
      } else {
        newSet.delete(assetId);
      }
      return newSet;
    });
  };

  // Handle select all filtered assets
  const handleSelectAllFiltered = (checked: boolean) => {
    setSelectedAssetIds(prev => {
      const newSet = new Set(prev);
      sortedAssets.forEach(asset => {
        if (checked) {
          newSet.add(asset.id);
        } else {
          newSet.delete(asset.id);
        }
      });
      return newSet;
    });
  };

  // Check if all filtered assets are selected
  const allFilteredSelected = sortedAssets.length > 0 && 
    sortedAssets.every(asset => selectedAssetIds.has(asset.id));

  // Handle confirm
  const handleConfirm = () => {
    const selectedAssets = availableAssets.filter(asset => selectedAssetIds.has(asset.id));
    onConfirm(selectedAssets);
    onOpenChange(false);
  };

  const canProceed = selectedAssetIds.size > 0;

  // Get badge variant based on status
  const getBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'default';
      case 'disposed':
        return 'destructive';
      case 'inactive':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  // Render asset list content based on loading/empty/data states
  const renderAssetListContent = () => {
    // Show empty state when not loading and no assets
    if (!isLoading && sortedAssets.length === 0) {
      return (
        <div className="flex items-center justify-center h-full min-h-[400px] text-muted-foreground">
          No assets match the current filters
        </div>
      );
    }

    return (
      <Table>
        <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
          <TableRow className="border-t-2 border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
            <TableHead className="h-12 font-semibold w-[40px] pl-6"></TableHead>
            <TableHead 
              className="h-12 font-semibold w-[120px] cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700"
              onClick={() => handleSort('code')}
            >
              Code{getSortIcon('code')}
            </TableHead>
            <TableHead 
              className="h-12 font-semibold min-w-[200px] cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700"
              onClick={() => handleSort('name')}
            >
              Name{getSortIcon('name')}
            </TableHead>
            <TableHead 
              className="h-12 font-semibold w-[150px] cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700"
              onClick={() => handleSort('category')}
            >
              Category{getSortIcon('category')}
            </TableHead>
            <TableHead 
              className="h-12 font-semibold w-[150px] cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700"
              onClick={() => handleSort('subCategory')}
            >
              Sub Category{getSortIcon('subCategory')}
            </TableHead>
            <TableHead 
              className="h-12 font-semibold w-[150px] cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700"
              onClick={() => handleSort('location')}
            >
              Location{getSortIcon('location')}
            </TableHead>
            <TableHead 
              className="h-12 font-semibold w-[150px] cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700"
              onClick={() => handleSort('subLocation')}
            >
              Sub Location{getSortIcon('subLocation')}
            </TableHead>
            <TableHead 
              className="h-12 font-semibold w-[120px] cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700"
              onClick={() => handleSort('postingFrequency')}
            >
              Posting Frequency{getSortIcon('postingFrequency')}
            </TableHead>
            <TableHead 
              className="h-12 font-semibold text-right w-[140px] cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700"
              onClick={() => handleSort('netBookValue')}
            >
              Opening NBV{getSortIcon('netBookValue')}
            </TableHead>
            <TableHead 
              className="h-12 font-semibold w-[120px] pr-6 cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700"
              onClick={() => handleSort('status')}
            >
              Status{getSortIcon('status')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedAssets.map((asset, index) => (
            <TableRow
              key={asset.id}
              className={`
                ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800
              `}
            >
              <TableCell className="pl-6">
                <Checkbox
                  checked={selectedAssetIds.has(asset.id)}
                  onCheckedChange={(checked) => handleSelectAsset(asset.id, checked as boolean)}
                  aria-label={`Select ${asset.code}`}
                />
              </TableCell>
              <TableCell className="font-medium">{asset.code}</TableCell>
              <TableCell>{asset.name}</TableCell>
              <TableCell>{asset.category}</TableCell>
              <TableCell className="text-muted-foreground">{asset.subCategory ?? '-'}</TableCell>
              <TableCell>{asset.location}</TableCell>
              <TableCell className="text-muted-foreground">{asset.subLocation ?? '-'}</TableCell>
              <TableCell className="text-muted-foreground">{asset.postingFrequency ?? '-'}</TableCell>
              <TableCell className="text-right">£{asset.netBookValue.toFixed(2)}</TableCell>
              <TableCell className="pr-6">
                <Badge variant={getBadgeVariant(asset.status)}>
                  {ASSET_STATUS_LABELS[asset.status] || asset.status}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[1400px] h-[90vh] flex flex-col">
        <DialogHeader className="text-left">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
          {/* Compact Filter Bar - Left Aligned */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Search - Takes priority */}
            <div className="relative flex-1 min-w-[280px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                placeholder="Search assets by code, name, or serial..."
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchTerm(e.target.value)}
                className="pl-9 h-9"
              />
            </div>

            {/* Filter Dropdowns - Compact */}
            <Select
              value={filters.category?.[0] ?? 'all'}
              onValueChange={(value: string) => {
                setFilters({ ...filters, category: value === 'all' ? undefined : [value] });
                setCurrentPage(1);
              }}
            >
              <SelectTrigger id="category" className="w-[160px] h-9">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {availableCategories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.subCategory?.[0] ?? 'all'}
              onValueChange={(value: string) => {
                setFilters({ ...filters, subCategory: value === 'all' ? undefined : [value] });
                setCurrentPage(1);
              }}
            >
              <SelectTrigger id="subCategory" className="w-[160px] h-9">
                <SelectValue placeholder="Sub Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sub categories</SelectItem>
                {availableSubCategories.map((subCategory) => (
                  <SelectItem key={subCategory} value={subCategory}>
                    {subCategory}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.location?.[0] ?? 'all'}
              onValueChange={(value: string) => {
                setFilters({ ...filters, location: value === 'all' ? undefined : [value] });
                setCurrentPage(1);
              }}
            >
              <SelectTrigger id="location" className="w-[160px] h-9">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All locations</SelectItem>
                {availableLocations.map((location) => (
                  <SelectItem key={location} value={location}>
                    {location}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.subLocation?.[0] ?? 'all'}
              onValueChange={(value: string) => {
                setFilters({ ...filters, subLocation: value === 'all' ? undefined : [value] });
                setCurrentPage(1);
              }}
            >
              <SelectTrigger id="subLocation" className="w-[160px] h-9">
                <SelectValue placeholder="Sub Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sub locations</SelectItem>
                {availableSubLocations.map((subLocation) => (
                  <SelectItem key={subLocation} value={subLocation}>
                    {subLocation}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.postingFrequency?.[0] ?? 'all'}
              onValueChange={(value: string) => {
                setFilters({ ...filters, postingFrequency: value === 'all' ? undefined : [value] });
                setCurrentPage(1);
              }}
            >
              <SelectTrigger id="postingFrequency" className="w-[180px] h-9">
                <SelectValue placeholder="Posting Frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All frequencies</SelectItem>
                {availablePostingFrequencies.map((frequency) => (
                  <SelectItem key={frequency} value={frequency}>
                    {frequency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Selection Info Bar */}
          <div className="flex items-center justify-between py-2.5 px-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="select-all-filtered"
                  checked={allFilteredSelected}
                  onCheckedChange={handleSelectAllFiltered}
                />
                <Label htmlFor="select-all-filtered" className="cursor-pointer text-sm font-medium">
                  Select all {sortedAssets.length} (filtered)
                </Label>
              </div>
              <div className="h-4 w-px bg-slate-300 dark:bg-slate-600" />
              <Badge variant="secondary" className="font-semibold">
                {selectedAssetIds.size} selected
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground">
              Showing {paginatedAssets.length} of {sortedAssets.length} {sortedAssets.length === 1 ? 'asset' : 'assets'}
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Asset List */}
          <div className="flex-1 overflow-auto rounded-lg min-h-[400px] border border-slate-200 dark:border-slate-700 relative">
            {/* Loading Spinner Overlay */}
            {isLoading && (
              <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-20 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Loading assets...</p>
                </div>
              </div>
            )}
            {renderAssetListContent()}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between py-2 px-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="h-8"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="h-8"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!canProceed}>
            {confirmButtonLabel} ({selectedAssetIds.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
